
import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, CardBody, CardHeader, Button, Spinner, InputGroup, InputGroupAddon, InputGroupText, Input,  ListGroup, ListGroupItem, Table } from "reactstrap";
import DumbellPlot from '../../components/SATI/DumbellPlot';
import AreaChart from '../../components/SATI/AreaChart';
import DateDropdown from '../../components/SATI/DateDropdown';
import DataTable from '../../components/SATI/DataTable';
import AlertModal from '../../components/SATI/AlertModal';
import { getUserHeartRate, getUserOxygenByCompany } from '../../helpers/satiBack';
import { getRefValue, createTable} from '../../helpers/levelsHelper';
import useReportFilter from "../../hooks/useReportFilter";
import { 
  calculatePressureLoad, 
  calculatePressureStats, 
  formatPressureData 
} from "../../utils/calculateUserLevelsMetrics";

import "chartist/dist/scss/chartist.scss";

const UserLevels = ({ user }) => {
  const [userData, setUserData] = useState([]);
  const [userHeartRate, setUserHeartRate] = useState([]);
  const [loadingBtn, setLoadingBtn] = useState(0);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [cargaSist, setCargaSist] = useState(0)
  const [cargaDiast, setCargaDiast] = useState(0)
  const [promediosSist, setPromediosSist] = useState([])
  const [promediosDiast, setPromediosDiast] = useState([])
  const [userOxygenData, setOxygenUserData] = useState([]);

  const getUserLevels = (loading, start, end) => {
    setLoadingBtn(loading);
    getUserHeartRate(start, end, user.usuarioId)
      .then(data => {
        data = data.filter(d => d.Valor > 0);
        setUserData(data)
        setLoadingBtn(0);
      })
      .catch(err => {
        console.error(err);
        setLoadingBtn(0);
      });

    getUserOxygenByCompany(start, end, user.usuarioId)
      .then(data => {
        data = data.filter(d => d.oxygen > 0);
        setOxygenUserData(data)
        setLoadingBtn(0);
      })
      .catch(err => {
        console.error(err);
        setLoadingBtn(0);
      });
  }

  const {
    range: dateRange,
    setRange: setDateRange,
    setWeekly,
    setMonthly,
    setCustom
  } = useReportFilter({
    onFilterChange: (id, start, end) => getUserLevels(id, start, end)
  });

  useEffect(() => {
    setWeekly();
  }, []);

  const dataPressure = useMemo(() => formatPressureData(userData), [userData]);

  const dataFreq = useMemo(() => 
    userData.map(d => ({
      date : d.HistoryDate,
      frecuencia : d.Valor,
    })), [userData]);

  const calcSist = (event) =>{
    setCargaSist(calculatePressureLoad(dataPressure, 'bloodPressure1', event.target.value));
  }

  const calcDiast = (event) =>{
    setCargaDiast(calculatePressureLoad(dataPressure, 'bloodPressure', event.target.value));
  }

  const dataOxygen = userOxygenData;

  const CustomRangeReport = () => {
    const start = dateRange[0].clone().date(1);
    const end = dateRange[1].clone().endOf('month');

    if (start.isAfter(end)) {
      setShowAlertModal(true);
      return;
    }

    setCustom([start, end]);
  }

  useEffect(() => {
    setPromediosDiast(calculatePressureStats(dataPressure, 'bloodPressure'));
    setPromediosSist(calculatePressureStats(dataPressure, 'bloodPressure1'));
    createTable(dataPressure)
  }, [dataPressure])

  const TableDataMed = {
    columns: [
      {
        label: 'Fecha',
        field: 'Fecha',
        sort: 'asc',
        width: 100
      },
      {
        label: 'Promedio Sistólica',
        field: 'PromSist',
        // sort: 'asc',
        width: 150
      },
      {
        label: 'Promedio Diastólica',
        field: 'PromDiast',
        // sort: 'asc',
        width: 150
      },
      {
        label: 'Número de mediciones',
        field: 'NumMediciones',
        // sort: 'asc',
        width: 150
      },
      {
        label: 'Ver Detalle',
        field: 'Detalle',
        // sort: 'asc',
        width: 150
      }
    ],
    rows: userData.map(d => ({
      Fecha : d.HistoryDate.getDate() + '/' + (d.HistoryDate.getMonth()+1) + '/' + d.HistoryDate.getFullYear(),
      PromSist : parseInt(d.Valor)+20,
      PromDiast: parseInt(d.Valor),
      NumMediciones: 1,
      Detalle: 'v'
    }))
  }

  return (
    <React.Fragment>
      <div className="container-fluid">
        <Row className="">
          <Col>
            <div className="page-title-box">
              <h4 className="font-size-20 text-left">{user.nombre}</h4>
            </div>
          </Col>
        </Row>
        
        <Row className="mb-4">
          <Col className="d-flex justify-content-center align-items-center flex-wrap">
            <Button onClick={setWeekly} color='primary' className='btn btn-primary waves-effect waves-light mx-1'>
              {loadingBtn === 1 && <Spinner size="sm" color="light" className="mr-2"/>}
              <span>Reporte semanal</span>
            </Button>
            <Button onClick={setMonthly} color='primary' className='btn btn-primary waves-effect waves-light mx-1'>
              {loadingBtn === 2 && <Spinner size="sm" color="light" className="mr-2"/>}
              <span>Reporte mensual</span>
            </Button>
            <div className="d-flex align-items-center m-2">
              <DateDropdown dateRange={dateRange} setDate={setDateRange}/>
              <span style={{ margin: '0 4px' }}>–</span>
              <DateDropdown dateRange={dateRange} setDate={setDateRange} end/>
              <Button onClick={CustomRangeReport} color='primary' className='btn btn-primary waves-effect waves-light mx-1'>
                {loadingBtn === 3 && <Spinner size="sm" color="light" className="mr-2"/>}
                <span>Reporte por rango</span>
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={8} md={12}>
            <AreaChart
            title = "Frecuencia Cardiaca"
            id="frecuencia"
            data={dataFreq}
            popoverTitle="Prueba"
            popoverBody="Cuerpo prueba"
            valueName="frecuencia"
            />
          </Col>

          <Col lg={4} md={12}>
          <Card>
            <CardHeader className="mt-0">
              <h5>Alertas</h5>
            </CardHeader>
            <CardBody>
              <ListGroup 
                style={{ minHeight: "340px", maxHeight: "340px", marginBottom: "10px", overflow:"scroll"}}
              >
                <ListGroupItem>Próximamente</ListGroupItem>
              </ListGroup>
            </CardBody>
          </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={8} md={12}>
            <AreaChart
            title = "Saturación de oxígeno"
            id="oxygen"
            data={dataOxygen}
            popoverTitle="Prueba"
            popoverBody="Cuerpo prueba"
            valueName="oxygen"
            />
          </Col>

          <Col lg={4} md={12}>
          <Card>
            <CardHeader className="mt-0">
              <h5>Alertas</h5>
            </CardHeader>
            <CardBody>
              <ListGroup 
                style={{ minHeight: "340px", maxHeight: "340px", marginBottom: "10px", overflow:"scroll"}}
              >
                <ListGroupItem>Próximamente</ListGroupItem>
              </ListGroup>
            </CardBody>
          </Card>
          </Col>
        </Row>
        
        <>
          { // Under feature flag
            process.env.REACT_APP_FEATURE_HEALTH_BLOOD_PRESSURE === true &&
            <>
              <Row> 
                <Col lg={8} md={12}>
                  <DumbellPlot
                  title = "Presión arterial"
                  data = {dataPressure}
                  id="pressGraph"
                  refUp = {getRefValue('arterialUp')}
                  refDown = {getRefValue('arterialDown')}
                  popoverTitle="Prueba"
                  popoverBody="Cuerpo prueba"
                  />
                </Col>

                <Col lg={4} md={12}>
                  <Row>
                    <Col>
                    <div>
                        <div className="mt-0">
                          <h5>Promedios</h5>
                        </div>
                        <CardBody>
                          <Table responsive striped >
                            <thead>
                              <tr>
                                <th></th>
                                <th>Promedio</th>
                                <th>Min</th>
                                <th>25%</th>
                                <th>75%</th>
                                <th>Max</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <th scope="row">Sistólica</th>
                                <td>{String(Math.round(promediosSist[0]))}</td>
                                <td>{promediosSist[1]}</td>
                                <td>{promediosSist[2]}</td>
                                <td>{promediosSist[3]}</td>
                                <td>{promediosSist[4]}</td>
                              </tr>
                              <tr>
                                <th scope="row">Diastólica</th>
                                <td>{String(Math.round(promediosDiast[0]))}</td>
                                <td>{promediosDiast[1]}</td>
                                <td>{promediosDiast[2]}</td>
                                <td>{promediosDiast[3]}</td>
                                <td>{promediosDiast[4]}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </CardBody>
                      </div>
                      <div>
                        <div className="mt-0">
                          <h5>Carga hipertensiva</h5>
                        </div>
                        <CardBody>
                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>Sistólica arriba de:</InputGroupText>
                            </InputGroupAddon>
                            <Input onChange={calcSist}/>
                            <InputGroupAddon addonType="append">
                              <InputGroupText>mmHg</InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                          <br/>
                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>Carga:</InputGroupText>
                            </InputGroupAddon>
                            <Input placeholder="" disabled value={cargaSist}/>
                            <InputGroupAddon addonType="append">
                              <InputGroupText>%</InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                          <br/><br/>
                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>Diastólica arriba de:</InputGroupText>
                            </InputGroupAddon>
                            <Input onChange={calcDiast}/>
                            <InputGroupAddon addonType="append">
                              <InputGroupText>mmHg</InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                          <br/>
                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>Carga:</InputGroupText>
                            </InputGroupAddon>
                            <Input placeholder="" disabled value={cargaDiast}/>
                            <InputGroupAddon addonType="append">
                              <InputGroupText>%</InputGroupText>
                            </InputGroupAddon>
                          </InputGroup>
                        </CardBody>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col lg={12} md={12}>
                  <DataTable 
                    title="Mediciones" 
                    tableData={TableDataMed}
                    id="mediciones"
                    popoverTitle="Mediciones"
                    popoverBody="Test"
                  />
                </Col>
              </Row>
            </>
          }
        </>

        <AlertModal show={showAlertModal} toggle={() => setShowAlertModal(!showAlertModal)}>
          El rango de meses debería ser válido!
        </AlertModal>

      </div>
    </React.Fragment>
  );
}

export default UserLevels;
